/**
 * ApiError — the single typed error thrown for every non-2xx response.
 * Carries the backend's stable machine `code` (the SPA branches on it,
 * never on message text) plus field details and the requestId for support.
 */

export interface ApiFieldError {
  field: string;
  message: string;
}

export interface ApiErrorShape {
  code: string;
  message: string;
  details?: unknown;
  requestId?: string;
}

export class ApiError extends Error {
  constructor(
    public code: string,
    message: string,
    public status: number,
    public details?: unknown,
    public requestId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** Field errors (422 validation_error) in react-hook-form friendly shape. */
  get fieldErrors(): ApiFieldError[] {
    return Array.isArray(this.details) ? (this.details as ApiFieldError[]) : [];
  }
}

/** Map error codes the UI handles specially to user-facing messages. */
const CODE_MESSAGES: Record<string, string> = {
  unauthorized: 'Your session has expired. Please sign in again.',
  forbidden: "You don't have permission to do that.",
  not_found: 'That item could not be found.',
  conflict: 'That conflicts with the current state — refresh and try again.',
  rate_limited: 'Too many requests — wait a moment and retry.',
  otp_required: 'An OTP verification is required before saving.',
  otp_invalid: 'The OTP code is invalid or expired.',
  invalid_status_for_project: 'That status is not part of this project.',
  reminder_in_past: 'The reminder date must be in the future.',
};

export function userMessage(err: unknown): string {
  if (err instanceof ApiError) {
    return CODE_MESSAGES[err.code] ?? err.message;
  }
  return 'Something went wrong. Please try again.';
}
